import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  limit,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import type { UserProfile, UserRole } from '../../types/user';
import { isEnvAdminEmail, isAdminRole, normalizeUserRole } from '../../utils/userRole';

const USERS = 'users';

export interface ResolvedUserProfile extends UserProfile {

  /** Firestore document id (may differ from Firebase Auth uid). */
  firestoreDocId: string;
  profileSource: 'authUid' | 'emailQuery' | 'synthetic';
}

function mapUserDoc(docId: string, data: Record<string, unknown>): UserProfile {
  const roleRaw = data.role ?? data.userRole ?? data.isAdmin;
  const role = normalizeUserRole(roleRaw);

  return {
    uid: docId,
    name:
      (data.name as string) ||
      (data.displayName as string) ||
      (data.systemName as string) ||
      '',
    email:
      (data.email as string) ||
      (data.companyEmail as string) ||
      (data.personalEmail as string) ||
      '',
    role,
    photoUrl: (data.photoUrl as string) || (data.passportPhotoUrl as string) || undefined,
    department: (data.department as string) || (data.departmentId as string) || undefined,
    designation: (data.designation as string) || (data.designationId as string) || undefined,
  };
}

async function findProfileByEmail(email: string): Promise<UserProfile | null> {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return null;

  const fields = ['email', 'companyEmail', 'personalEmail'] as const;

  for (const field of fields) {
    try {
      const snap = await getDocs(
        query(collection(db, USERS), where(field, '==', email.trim()), limit(1)),
      );
      if (!snap.empty) {
        const docSnap = snap.docs[0];

        return mapUserDoc(docSnap.id, docSnap.data() as Record<string, unknown>);
      }
    } catch (error) {
      console.warn(`findProfileByEmail(${field}) failed`, error);
    }
  }

  return null;
}

/**
 * Resolve the signed-in user's profile.
 * Prefers an admin role from either users/{authUid} or a users doc matched by email
 * (JK Loom often stores companyEmail on a doc whose id is not the Auth uid).
 */
export async function getUserProfile(
  authUid: string,
  authEmail?: string | null,
): Promise<ResolvedUserProfile | null> {
  const email = authEmail?.trim() || '';

  let byUid: UserProfile | null = null;
  try {
    const snap = await getDoc(doc(db, USERS, authUid));
    if (snap.exists()) {
      byUid = mapUserDoc(snap.id, snap.data() as Record<string, unknown>);
    }
  } catch (error) {
    console.warn('getUserProfile by uid failed', error);
  }

  const byEmail = email ? await findProfileByEmail(email) : null;

  const pick = (): { profile: UserProfile; source: ResolvedUserProfile['profileSource']; docId: string } | null => {
    if (byEmail && isAdminRole(byEmail.role)) {
      return { profile: byEmail, source: 'emailQuery', docId: byEmail.uid };
    }
    if (byUid && isAdminRole(byUid.role)) {
      return { profile: byUid, source: 'authUid', docId: authUid };
    }
    if (byEmail) {
      return { profile: byEmail, source: 'emailQuery', docId: byEmail.uid };
    }
    if (byUid) {
      return { profile: byUid, source: 'authUid', docId: authUid };
    }

    return null;
  };

  const chosen = pick();
  if (chosen) {
    const role =
      isEnvAdminEmail(email) && !isAdminRole(chosen.profile.role) ? 'admin' : chosen.profile.role;

    return {
      ...chosen.profile,
      uid: authUid,
      email: chosen.profile.email || email,
      role,
      firestoreDocId: chosen.docId,
      profileSource: chosen.source,
    };
  }

  if (isEnvAdminEmail(email)) {
    return {
      uid: authUid,
      name: '',
      email,
      role: 'admin',
      firestoreDocId: authUid,
      profileSource: 'synthetic',
    };
  }

  return null;
}

export async function listUsers(): Promise<UserProfile[]> {
  const snap = await getDocs(collection(db, USERS));

  return snap.docs.map((d) => mapUserDoc(d.id, d.data() as Record<string, unknown>));
}

export async function updateUserRole(uid: string, role: UserRole): Promise<void> {
  await updateDoc(doc(db, USERS, uid), { role });
}

export async function upsertUserProfile(profile: Partial<UserProfile> & { uid: string }): Promise<void> {
  await setDoc(doc(db, USERS, profile.uid), profile, { merge: true });
}

export async function getUserDoc(userId: string): Promise<Record<string, unknown> | null> {
  const snap = await getDoc(doc(db, USERS, userId));
  if (!snap.exists()) {
    return null;
  }

  return snap.data() as Record<string, unknown>;
}

/** Mirrors Android UserRepository.applyWfhApproved — called when admin approves WFH. */
export async function applyWfhApproved(userId: string, wfhEndDate: string): Promise<void> {
  const data = await getUserDoc(userId);
  if (!data) {
    throw new Error('User not found');
  }
  const jobType = (data.jobType as string) || (data.workType as string) || 'office';
  const wfhRevertJobType = (data.wfhRevertJobType as string)
    || (jobType !== 'remote' ? jobType : 'office');
  const currentUntil = (data.wfhActiveUntil as string) || '';
  const newUntil = currentUntil >= wfhEndDate ? currentUntil : wfhEndDate;
  await updateDoc(doc(db, USERS, userId), {
    jobType: 'remote',
    workType: 'remote',
    wfhActiveUntil: newUntil,
    wfhRevertJobType,
  });
}

export { isAdminRole };
